import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { motion } from "framer-motion"
import { Sparkle } from "lucide-react"
import { MapPlusIcon, MapPlusIconActive } from "@/components/icons/MapPlusIcon"
import { SparkleIconActive } from "@/components/icons/SparkleIcon"

export type AnonymousTab = "plan" | "ai"

type AnonymousSidebarProps = {
  activeTab: AnonymousTab
  onTabChange: (tab: AnonymousTab) => void
}

type SidebarButtonProps = {
  active: boolean
  onClick: () => void
  icon: React.ComponentType<{ className?: string }>
  activeIcon: React.ComponentType<{ className?: string }>
  tooltipText: string
  /** Short label shown under the icon on the phone tab bar, where tooltips can't be hovered. */
  label: string
}

/**
 * Height of the phone tab bar. Content that sits in a fixed, full-height
 * container on the anonymous page has to leave this much room at the bottom
 * (`pb-14` / `bottom-14`) so the bar doesn't cover it.
 */
export const MOBILE_TAB_BAR_CLASS = "h-14"

const SidebarButton = ({
  active,
  onClick,
  icon: Icon,
  activeIcon: ActiveIcon,
  tooltipText,
  label,
}: SidebarButtonProps) => {
  const IconComponent = active ? ActiveIcon : Icon
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            aria-label={tooltipText}
            aria-current={active ? "page" : undefined}
            className={cn(
              "bg:transparent relative flex items-center justify-center rounded-[6px] hover:bg-sidebar",
              // Phone: wide tap target with a label; desktop rail: 32px icon square.
              "h-11 flex-1 flex-col gap-0.5 px-2 md:h-8 md:w-8 md:flex-none md:flex-row md:p-1.5",
            )}
          >
            {active && (
              <motion.span
                layoutId="anonymous-sidebar-bubble"
                className="absolute inset-0 z-10 bg-[#e5e5e5]"
                style={{ borderRadius: 6 }}
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
            <IconComponent className="z-20 h-5 w-5" />
            <span className="z-20 text-[11px] leading-none md:hidden">
              {label}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const AnonymousSidebar = ({
  activeTab,
  onTabChange,
}: AnonymousSidebarProps) => {
  const { t } = useTranslation()
  return (
    // Phone (<768px): a bottom tab bar across the full width, so the two
    // tabs don't cost 48px of a ~390px screen. Desktop: the fixed left rail.
    <nav
      aria-label={t("planOrder.anonymousSidebar.sections")}
      className={cn(
        "fixed z-[999] border-gray-200 bg-muted",
        `inset-x-0 bottom-0 ${MOBILE_TAB_BAR_CLASS} border-t pb-[env(safe-area-inset-bottom)]`,
        "md:inset-x-auto md:bottom-auto md:left-0 md:top-0 md:h-screen md:w-[48px] md:border-r md:border-t-0 md:pb-0",
      )}
    >
      <div className="flex h-full items-stretch gap-1 px-2 md:m-2 md:mt-4 md:h-auto md:flex-col md:gap-3 md:px-0">
        <SidebarButton
          active={activeTab === "plan"}
          onClick={() => onTabChange("plan")}
          icon={MapPlusIcon}
          activeIcon={MapPlusIconActive}
          tooltipText={t("planOrder.anonymousSidebar.planRouteOnMap")}
          label={t("planOrder.anonymousSidebar.planRoute")}
        />
        <SidebarButton
          active={activeTab === "ai"}
          onClick={() => onTabChange("ai")}
          icon={Sparkle}
          activeIcon={SparkleIconActive}
          tooltipText="Slick AI"
          label="Slick AI"
        />
      </div>
    </nav>
  )
}

export default AnonymousSidebar
